import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useLocation, useNavigate, useParams } from 'react-router';
import { BlockStack, Card, Layout, Modal } from '@shopify/polaris';

import TransferPageWrapper from './TransferPageWrapper';
import TransferHeader from './TransferHeader';
import TransferBreadcrumbs from './TransferBreadcrumbs';
import TransferItemEntry from './TransferItemEntry';
import TransferItemListing from './TransferItemListing';
import { useTransferStore, useTransferToast, useTransferHeader, useTransferActions } from '#client/stores/transfer/useTransferStore';
import { SessionContext } from '#client/contexts/SessionContext';
import ActivityLog from '#client/components/ActivityLog';
import { hasPermission } from '#client/utils/helpers';
import NoPermissions from '#client/pages/NoPermissions';

const Transfer = () => {
    const { carrier, displayTransferId, lines, locationToId, locationFromId, mode, originalTransfer, status, trackingNumber, editingIndex, type, resetLines } = useTransferHeader();

    const navigate = useNavigate();
    const { id: routeTransferId } = useParams();

    const { pathname } = useLocation();
    const { currentUser } = useContext(SessionContext);

    // Zustand store hooks
    const { initializeWithTransfer, setMode } = useTransferActions();
    const { setToastError } = useTransferToast();

    // Determine the mode based on the current pathname
    const getTransferMode = useCallback(() => {
        if (pathname.includes('/create')) return 'create';
        if (pathname.includes('/edit')) return 'edit';
        if (pathname.includes('/receive')) return 'receive';
        return 'view';
    }, [pathname]);
    let routeId = parseInt(routeTransferId, 10);
    // Initialize transfer state based on the route
    useEffect(() => {
        // Determine initial mode based on pathname
        let initialMode = getTransferMode();

        async function fetchInitializeWithTransfer() {
            try {
                await initializeWithTransfer(routeId, initialMode);
            } catch (error) {
                navigate('/transfers', { viewTransition: true });
            }
        }

        if (routeId && initialMode !== 'create') {
            fetchInitializeWithTransfer();
        }

        if (initialMode === 'create') {
            setMode('create');
        } else {
            setMode(getTransferMode());
        }

        useTransferStore.use.currentUserId = currentUser.email;
    }, [pathname, initializeWithTransfer, setMode, navigate, setToastError, getTransferMode]);

    const [shouldBlockNavigation, setShouldBlockNavigation] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);

    const isDirty = useMemo(() => {
        if (mode == 'create' && (locationToId != '' || carrier != '' || trackingNumber != '' || lines.length > 0)) {
            return true;
        } else if (mode == 'edit' && originalTransfer) {
            if (originalTransfer.shipmentCarrier != carrier) return true;
            if (originalTransfer.shipmentTrackingNo != trackingNumber) return true;
            if (originalTransfer.locationIdDestination != locationToId) return true;
            if (lines.length != originalTransfer.lines.length) return true;
            if (editingIndex != null && editingIndex >= 0) {
                return true;
            }

            for (let x = 0; x < lines.length; x++) {
                const line = lines[x];

                var originalLine = originalTransfer.lines.find((l) => {
                    return l.productId == line.productId && l.qtySent == line.qtySent;
                });
                if (!originalLine) return true;
            }
        }
    });

    const isNavigatingRef = useRef(false);

    useBlocker(({ currentLocation, nextLocation }) => {
        // Don't block if we're in the middle of a confirmed navigation
        if (isNavigatingRef.current) {
            isNavigatingRef.current = false;
            return false;
        }

        if (!isDirty) return false;

        if (currentLocation.pathname !== nextLocation.pathname) {
            setPendingNavigation(nextLocation);
            setShouldBlockNavigation(true);
            return true; // Block the navigation
        }

        return false;
    }, isDirty);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (shouldBlockNavigation) {
                event.preventDefault();
                event.returnValue = '';
                return '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [shouldBlockNavigation]);

    const handleNavigationCancel = useCallback(() => {
        setShouldBlockNavigation(false);
    }, []);

    const createStayOnPageAction = useCallback(
        () => ({
            content: 'Stay on page',
            onAction: () => {
                // if (currentEditingIndexRef.current !== null) {
                //     navigationModal.markRowTriedToNavigate(currentEditingIndexRef.current);
                // }
                //setSubmitClicked(false);
                handleNavigationCancel();
            },
            destructive: false,
        }),
        []
    );

    const handleNavigationConfirm = useCallback(() => {
        if (pendingNavigation) {
            if (mode == 'edit') {
                resetLines(originalTransfer.lines);
            }
            setShouldBlockNavigation(false);
            isNavigatingRef.current = true;

            const path = pendingNavigation.pathname + (pendingNavigation.search || '') + (pendingNavigation.hash || '');

            navigate(path, { replace: true });
            setPendingNavigation(null);
        }
    }, [pendingNavigation, navigate]);

    const modalConfig = {
        open: shouldBlockNavigation,
        onClose: handleNavigationCancel,
        title: 'Unsaved Changes',
        text: 'You have unsaved changes that will be lost if you leave this page. Are you sure you want to continue?',
        primaryAction: {
            content: 'Leave without saving',
            onAction: handleNavigationConfirm,
            destructive: true,
        },
        secondaryActions: [createStayOnPageAction()],
    };

    const locationIds = currentUser.locationIds;
    if (['edit', 'view', 'receive'].indexOf(getTransferMode()) != -1) {
        if (locationFromId && locationFromId != '' && locationToId != '' && locationIds.indexOf(locationFromId) == -1 && locationIds.indexOf(locationToId) == -1) {
            return <NoPermissions permission="transfer" />;
        }
    }

    let displayItemEntry = false;
    if (status == 'Initiated' || status == 'Complete') {
        displayItemEntry = false;
    } else if ((getTransferMode() == 'create' && locationFromId) || (getTransferMode() == 'edit' && locationFromId && type != 'Generated')) {
        displayItemEntry = true;
    } else if (getTransferMode() == 'receive') {
        displayItemEntry = true;
    }

    if (locationFromId && getTransferMode() == 'edit' && !hasPermission(currentUser, 'transferUpdate')) {
        return <NoPermissions permission="missing_role" />;
    }

    return (
        <>
            <TransferBreadcrumbs />
            <TransferPageWrapper>
                <Layout>
                    <Layout.Section>
                        <TransferHeader ref={isNavigatingRef} />
                    </Layout.Section>
                    {displayItemEntry && (
                        <Layout.Section>
                            <TransferItemEntry />
                        </Layout.Section>
                    )}
                    {locationFromId && (
                        <Layout.Section>
                            <TransferItemListing />
                        </Layout.Section>
                    )}
                    {displayTransferId && (
                        <Layout.Section>
                            <Card>
                                <ActivityLog objectType="InventoryTransfer" objectId={displayTransferId} />
                            </Card>
                        </Layout.Section>
                    )}
                </Layout>
            </TransferPageWrapper>
            <Modal {...modalConfig}>
                <Modal.Section>
                    <BlockStack>
                        <p>{modalConfig.text}</p>
                    </BlockStack>
                </Modal.Section>
            </Modal>
        </>
    );
};

export default Transfer;
